<template>
  <div id="sidepanel">
    <v-layout row wrap no-height>
      <v-flex id="sidepanel-scrollable" class="ma-3">
        <v-card v-if="dates.length && !hide">
          <v-card-text class="pt-0 pb-0">
            <v-tabs>
              <v-tab v-html="$t('Person search')">
              </v-tab>
              <v-tab v-html="$t('Address search')">
              </v-tab>
              <v-tab v-html="$t('Area search')" @change="showLockdowns=true">
              </v-tab>
              <v-tab-item :transition="false" :reverse-transition="false">
                <v-autocomplete
                  v-model="selectedPerson"
                  :items="personAutoCompleteEntries"
                  item-text="azon"
                  item-value="_id"
                  :loading="isPersonAutoCompleteLoading"
                  :search-input.sync="personAutoCompleteSearch"
                  hide-no-data
                  clearable
                  return-object
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title v-text="item.azon"></v-list-item-title>
                      <v-list-item-subtitle v-text="itemAddress(item)"></v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-tab-item>
              <v-tab-item :transition="false" :reverse-transition="false">
                <v-autocomplete
                  ref="addressAutoComplete"
                  @input="fitMap"
                  :items="addressAutoCompleteEntries"
                  item-text="address"
                  :loading="isAddressAutoCompleteLoading"
                  :search-input.sync="addressAutoCompleteSearch"
                  hide-no-data
                  clearable
                  return-object
                ></v-autocomplete>
              </v-tab-item>
              <v-tab-item :transition="false" :reverse-transition="false">
                <v-autocomplete
                  ref="polygonAutoComplete"
                  @input="fitMapPolygon"
                  :items="polygonAutoCompleteEntries"
                  item-text="properties.name"
                  :loading="isPolygonAutoCompleteLoading"
                  :search-input.sync="polygonAutoCompleteSearch"
                  hide-no-data
                  clearable
                  return-object
                ></v-autocomplete>
              </v-tab-item>
            </v-tabs>

            <v-subheader
              class="pa-0"
              style="height:32px"
            >{{$t('Date')}}: {{dateText(new Date(this.selectedDate).toISOString())}}</v-subheader>
            <v-slider
              v-model="dateIndex"
              :max="dates.length-1"
              step="1"
              ticks
              v-on:change="dateChanged"
            >
              <template v-slot:append>
                <v-icon v-if="!isPlaying" @click="play">play_circle_outline</v-icon>
                <v-icon v-if="isPlaying" @click="stop">pause_circle_outline</v-icon>
              </template>
              <template v-slot:prepend>
                <v-menu
                  v-model="datePickerMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on">date_range</v-icon>
                  </template>
                  <v-date-picker
                    :value="selectedDate"
                    :max="maxDate"
                    :min="minDate"
                    no-title
                    locale="hu"
                    first-day-of-week="1"
                    @input="dateChangedDatePicker"
                  ></v-date-picker>
                </v-menu>
              </template>
            </v-slider>
            <v-select id="map-types" v-model="mapMode" :items="mapTypes" dense hide-details></v-select>

            <v-subheader class="mt-5 pa-0" style="height:32px">
              {{$t('Statuses')}}
              <v-spacer></v-spacer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" x-small icon @click="selectAllStatusesClicked">
                    <v-icon>{{allStatusSelected ? 'clear' : 'done'}}</v-icon>
                  </v-btn>
                </template>
                <span>{{allStatusSelected ? $t('Remove all statuses') : $t('Add all statuses')}}</span>
              </v-tooltip>
            </v-subheader>
            <v-select
              id="statuses"
              class="ma-0 status-selector"
              item-text="label"
              v-model="selectedStatuses"
              :items="translatedMapStatuses"
              @change="statusSelectorChanged"
              multiple
              chips
              dense
              hide-details
            >
              <template v-slot:selection="{ item }">
                <v-chip outlined>
                  <v-icon class="mr-1" x-small :color="item.color">lens</v-icon>
                  {{ item.label }}
                </v-chip>
              </template>
            </v-select>
            <v-switch
              dense
              v-model="homeQuarantine"
              :label="$t('Home quarantine visible')"
              :disabled="mapMode!='heatmap'"
              hide-details
            ></v-switch>
            <v-switch
              class="mt-1"
              dense
              v-model="byPrimaryAddress"
              hide-details
              :label="$t('By primary address')"
            ></v-switch>
            <v-switch class="mt-1" dense v-model="showLockdowns" :label="$t('Closed areas')"></v-switch>
          </v-card-text>
        </v-card>
      </v-flex>
      <v-flex class="mt-4 no-height">
        <v-icon
          color="primary"
          large
          class="mt-1"
          @click="hide=!hide"
        >{{hide ? 'mdi-arrow-right-bold-box': 'mdi-arrow-left-bold-box'}}</v-icon>
      </v-flex>
    </v-layout>
  </div>
</template>

<style lang="scss" scoped>
@import "./SidePanel.scss";
</style>

<script>
import moment from "moment-mini";
import debounce from "lodash.debounce";
import mapboxgl from "mapbox-gl";
import ApiService from "../services/ApiService";
import { mapState } from "vuex";
import { getDates, dateText } from "../helpers/dateFormat";
import { DATA_TRANSLATIONS } from "../helpers/dataTranslations";

export default {
  name: "SidePanel",
  props: ["map"],
  data() {
    return {
      dateText:dateText,
      datePickerMenu: false,
      dates: [],
      dateIndex: undefined,
      selectedDate: undefined,
      maxDate: undefined,
      minDate: undefined,
      statuses: [],
      isPersonAutoCompleteLoading: false,
      personAutoCompleteEntries: [],
      personAutoCompleteSearch: "",
      isAddressAutoCompleteLoading: false,
      addressAutoCompleteEntries: [],
      addressAutoCompleteSearch: "",
      isPolygonAutoCompleteLoading: false,
      polygonAutoCompleteEntries: [],
      polygonAutoCompleteSearch: "",

      isPlaying: false,
      hide: false,
      allStatusSelected: true
    };
  },

  beforeDestroy() {
    this.stop();
  },

  watch: {
    dateIndex() {
      this.selectedDate = this.dates[this.dateIndex].toISOString();
    },
    personAutoCompleteSearch(value) {
      if (!value) {
        return;
      }
      this.refreshPersonEntries(value);
    },
    addressAutoCompleteSearch(value) {
      if (!value) {
        return;
      }

      this.refreshAddressEntries(value);
    },
    polygonAutoCompleteSearch(value) {
      if (!value) {
        return;
      }
      this.refreshPolygonEntries(value);
    },
    selectedPerson(value) {
      if (value) {
        this.personAutoCompleteSearch = this.selectedPerson.azon;
      }
    },
    selectedAddress(value) {
      if (value) {
        this.addressAutoCompleteSearch = this.selectedAddress;
      }
    },
    selectedPolygon(value) {
      if (value) {
        this.polygonAutoCompleteSearch = this.selectedPolygon;
      }
    },

    config(config) {
      if (config) {
        this.statuses = config.statuses;
      }
    }
  },

  mounted() {
    if (this.config) {
      this.statuses = this.config.statuses;
    }

    const datesResult = getDates();
    this.dates = datesResult.dates;
    this.minDate = datesResult.minDate;
    this.maxDate = datesResult.maxDate;

    this.dateIndex = this.dates.length - 1;

    this.dateChangedDatePicker(this.$store.state.map.date);

    if (this.selectedPerson) {
      this.personAutoCompleteSearch = this.selectedPerson.azon;
    }
    this.dateChanged();
  },

  computed: {
    ...mapState({
      config: state => state.app.config
    },
    ),
    mapStatuses() {
      return this.statuses.filter(s => s.showOnMap);
    },
    translatedMapStatuses() {
      return this.mapStatuses.map(x => {
        return { ...x, label: this.$t(DATA_TRANSLATIONS[x.value]) }
      })
    },
    mapTypes() {
      return [
        { text: this.$t("Heatmap"), value: "heatmap" },
        { text: this.$t("Clustered data"), value: "cluster" },
        { text: this.$t("Contact locations"), value: "places" }
      ]
    },
    mapMode: {
      get() {
        return this.$store.state.map.mode;
      },
      set(value) {
        this.$store.commit("map/setMode", value);
      }
    },
    selectedStatuses: {
      get() {
        return this.$store.state.map.activeStatuses;
      },
      set(value) {
        this.$store.commit("map/setActiveStatuses", value);
      }
    },
    homeQuarantine: {
      get() {
        return (
          this.$store.state.map.homeQuarantine && this.mapMode == "heatmap"
        );
      },
      set(value) {
        this.$store.commit("map/setHomeQuarantine", value);
      }
    },
    byPrimaryAddress: {
      get() {
        return this.$store.state.map.filterByPrimaryAddress;
      },
      set(value) {
        this.$store.commit("map/setFilterByPrimaryAddress", value);
      }
    },
    showLockdowns: {
      get() {
        return this.$store.state.map.showLockdowns;
      },
      set(value) {
        this.$store.commit("map/setShowLockdowns", value);
      }
    },
    selectedPerson: {
      get() {
        return this.$store.state.map.activePerson;
      },
      set(value) {
        this.$store.commit("map/setActivePerson", value);
      }
    }
  },

  methods: {
    selectAllStatusesClicked() {
      if (this.allStatusSelected) {
        this.selectedStatuses = [];
      } else {
        this.selectedStatuses = this.mapStatuses.map(x => x.value);
      }

      this.allStatusSelected = !this.allStatusSelected;
    },

    statusSelectorChanged(value) {
      if (!value.length) {
        this.allStatusSelected = false;
      }

      if (value.length === this.mapStatuses.length) {
        this.allStatusSelected = true;
      }
    },

    fitMap(value) {
      if (value) {
        this.map.fitBounds(value.bbox);

        this.map.once("dragend", () => {
          this.addressAutoCompleteSearch = "";
          this.addressAutoCompleteEntries = [];
          this.$refs.addressAutoComplete.blur();
        });
      }
    },

    fitMapPolygon(value) {
      if (value && value.geometry && value.geometry.coordinates) {
        var coordinates = value.geometry.coordinates;

        var bounds = coordinates[0].reduce(function(bounds, coord) {
          return bounds.extend(coord);
        }, new mapboxgl.LngLatBounds(coordinates[0][0], coordinates[0][0]));

        this.map.fitBounds(bounds, {
          padding: 20
        });

        this.map.once("dragend", () => {
          this.polygonAutoCompleteSearch = null;
          this.polygonAutoCompleteEntries = [];
          this.$refs.polygonAutoComplete.blur();
        });
      }
    },

    play() {
      this.isPlaying = true;

      const recursive = () => {
        setTimeout(() => {
          if (this.isPlaying) {
            if (this.dateIndex == this.dates.length - 1) {
              this.dateIndex = -1;
            }
            this.dateIndex += 1;
            this.dateChanged();

            recursive();
          }
        }, 1000);
      };

      recursive();
    },
    stop() {
      this.isPlaying = false;
    },
    dateChanged() {
      this.$store.commit("map/setDate", this.dates[this.dateIndex]);
    },
    dateChangedDatePicker(value) {
      const first = moment(this.dates[0]);
      const current = moment(value);

      this.dateIndex = current.diff(first, "days");
      this.datePickerMenu = false;
      this.dateChanged();
    },
    refreshPersonEntries: debounce(async function(value) {
      this.isPersonAutoCompleteLoading = true;
      try {
        const response = await ApiService.searchPeopleByAzon(value, {
          forMap: true
        });
        this.personAutoCompleteEntries = response.data;
      } catch (ex) {
        this.personAutoCompleteEntries = [];
      } finally {
        this.isPersonAutoCompleteLoading = false;
      }
    }, 300),
    refreshAddressEntries: debounce(async function(value) {
      this.isAddressAutoCompleteLoading = true;
      try {
        const response = await ApiService.locationSearch(value);
        const result = [];
        response.data.result.features.forEach(item => {
          result.push(item.properties);
        });
        this.addressAutoCompleteEntries = result;
      } catch (ex) {
        this.addressAutoCompleteEntries = [];
      } finally {
        this.isAddressAutoCompleteLoading = false;
      }
    }, 300),
    refreshPolygonEntries: debounce(async function(value) {
      this.isPolygonAutoCompleteLoading = true;
      try {
        const response = await ApiService.polygonSearch({
          query: value,
          date: this.selectedDate
        });
        this.polygonAutoCompleteEntries = response.data;
      } catch (ex) {
        this.polygonAutoCompleteEntries = [];
      } finally {
        this.isPolygonAutoCompleteLoading = false;
      }
    }, 300),
    itemAddress(item) {
      let address = "";
      if (item && item.latest && item.latest.primaryAddress) {
        address = item.latest.primaryAddress.address;
      } else if (item && item.latest && item.latest.place) {
        address = item.latest.place.address;
      }
      return address;
    }
  }
};
</script>
